import React, { useEffect, useMemo, useState } from 'react';
import BackButton from '../BackButton';
import { Tooltip, Typography } from '@material-ui/core';
import { IconButton} from '@coolremote/shared';
import { t } from 'ttag';
import TableWithConfig from '@components/TableWithConfig';
import { Delete } from '@material-ui/icons';
import {
  useDeleteBacnetUiTemplate,
  useGetSiteBacnetUiTemplates,
  useUpdateBacnetUiTemplate,
} from '@hooks/useBacnetUiTemplatesApi';
import useBacNet from '@hooks/useBacNet';
import { useStoreActions, useStoreState } from '@models/RootStore';
import { useConfirmation } from '@coolremote/shared';
import ImportExportDialog from './components/ImportExportDialog';
import { readJsonFile } from '@services/utils';
import ExportIcon from './components/ExportIcon';
import ImportIcon from './components/ImportIcon';

import useStyles from './styles';
import EditInPlaceCell from './components/EditInPlaceCell';

export const TEMPLATE_TYPE = {
  PARAM_TEMPLATE: 0,
  UI_TEMPLATE: 1,
};

export const DISPLAY_TEMPLATE_TYPE = {
  [TEMPLATE_TYPE.PARAM_TEMPLATE]: t`Parameter Template`,
  [TEMPLATE_TYPE.UI_TEMPLATE]: t`UI Template`,
};

export const nestingConfig = {
  childrenKey: 'uiTemplates',
};

const getDeleteBodyMessage = (row) => {
  const isUiTemplate = row.type === TEMPLATE_TYPE.UI_TEMPLATE;
  const templateName = row.name;

  const numberOfAssociatedUiTemplates = row.uiTemplates
    ? row.uiTemplates.length
    : 0;

  if (isUiTemplate) {
    return t`Are you sure you want to delete the UI Template "${templateName}"? This action cannot be undone.`;
  } else {
    return (
      <div>
        <div>{t`Deleting the Parameter Template:`}</div>
        <b>{templateName}</b>
        <div>{t`will also delete`}</div>
        <b>
          {numberOfAssociatedUiTemplates} {t`associated UI Templates.`}
        </b>
        <div>{t`Are you sure you want to proceed? This action cannot be undone.`}</div>
      </div>
    );
  }
};

export const parseTemplatesForExport = (templates) => {
  return templates.map((template) => {
    const { name, model, vendorId, data, uiTemplates, vendorName, entityTemplate } = template;
    const newTemplate = { name, model, vendorId, data, vendorName, entityTemplate, ['ui-templates']: [] };
    if (uiTemplates && uiTemplates.length > 0) {
      newTemplate['ui-templates'] = uiTemplates.map((uiTemplate) => {
        const { name, fileId, data } = uiTemplate;
        return { name, fileId, data };
      });
    }
    return newTemplate;
  });
};

const validateJSONForImport = (templateFileData) => {
  const { identifier, templates } = templateFileData;
  const result = {
    success:true,
    message:'',
  };

  if (!identifier || identifier !== 'BACnet templates export') {
    result.success = false;
    result.message = t`Invalid file format: Missing or incorrect identifier.`;
    return result;
  }
  if (!Array.isArray(templates)) {
    result.success = false;
    result.message = t`Invalid file format: 'templates' should be an array.`;
    return result;
  }
  if (templates.length === 0) {
    result.success = false;
    result.message = t`No templates found in the imported file.`;
    return result;
  }
  return result;
};

const validateTemplateName = (name ,originalName) => {

  if(name === originalName) {
    return { valid: true, message: '' };
  }

  if (name.trim() === originalName.trim()) {
    return { valid: false, message: '' };
  }

  if (!name || name.trim() === '') {
    return { valid: false, message: t`Template name cannot be empty.` };
  }

  if (name.length > 50) {
    return { valid: false, message: t`Template name cannot exceed 50 characters.` };
  }

  return { valid: true, message: '' };
};


const TemplateManagement = ({ onClose, vendorsMap, usedTemplates }) => {
  const classes = useStyles();

  const addMessage = useStoreActions(
    (action) => action.errorMessage.addMessage
  );

  const { siteId } = useStoreState((s) => s.selections.selections);
  const {
    getSiteBacNetParameterTemplates,
    siteBacNetParameterTemplates,
    deleteBacNetParameterTemplate,
    updateBacNetParameterTemplate,
  } = useBacNet();
  const { data: uiTemplates, refetch: refetchUiTemplates } =
    useGetSiteBacnetUiTemplates(siteId);

  const { mutate: updateUiTemplate } = useUpdateBacnetUiTemplate();

  const { mutate: deleteUiTemplate } = useDeleteBacnetUiTemplate();

  const confirm = useConfirmation();
  const [importExportDialogMode, setImportExportDialogMode] = useState(null);
  const [importedData, setImportedData] = useState(null);

  const templatesData = useMemo(() => {
    if (!siteBacNetParameterTemplates || !uiTemplates) return [];
    const templates = JSON.parse(JSON.stringify(siteBacNetParameterTemplates)); // Deep copy to avoid mutating state

    uiTemplates.forEach((uiTemplate) => {
      const templateId = uiTemplate.paramTemplate;

      if (!templateId || !templates[templateId]) {
        return;
      }

      const uiTemplateData = {
        ...uiTemplate,
        type: TEMPLATE_TYPE.UI_TEMPLATE,
        vendorId: templates[templateId].vendorId,
        vendorName:
          vendorsMap?.[templates[templateId].vendorId] || t`Unknown Vendor`,
        model: templates[templateId].model,
      };

      if (templates[templateId].uiTemplates) {
        templates[templateId].uiTemplates.push(uiTemplateData);
      } else {
        templates[templateId].uiTemplates = [uiTemplateData];
      }
    });

    return Object.values(templates).map((template) => {
      template.type = TEMPLATE_TYPE.PARAM_TEMPLATE;
      template.disableDelete = usedTemplates && usedTemplates[template.id];
      template.vendorName = vendorsMap?.[template.vendorId] || t`Unknown Vendor`;
      if (template.uiTemplates) {
        template.uiTemplates.forEach((uiTemplate) => {
          uiTemplate.disableDelete =
            usedTemplates && usedTemplates[uiTemplate.id];
        });
      }
      return template;
    });
  }, [siteBacNetParameterTemplates, uiTemplates, vendorsMap, usedTemplates]);

  useEffect(() => {
    refetchUiTemplates();
    getSiteBacNetParameterTemplates(siteId);
  }, [siteId]);

  const handleDeleteClick = (row) => async () => {
    const userConfirmed = await confirm({
      title: t`Delete Template`,
      bodyMessage: getDeleteBodyMessage(row),
      cancelText: t`Cancel`,
      confirmText: t`Delete`,
    });

    if (!userConfirmed) {
      return;
    }

    try {
      if (row.type === TEMPLATE_TYPE.UI_TEMPLATE) {
        await deleteUiTemplate(row.id);
      } else {
        // Delete all associated UI templates first
        if (row.uiTemplates && row.uiTemplates.length > 0) {
          for (const uiTemplate of row.uiTemplates) {
            await deleteUiTemplate(uiTemplate.id);
          }
        }
        // Then delete the parameter template
        await deleteBacNetParameterTemplate(row);
      }
      refetchUiTemplates();
      getSiteBacNetParameterTemplates(siteId);
    } catch (error) {
      console.error('Error deleting template:', error);
      addMessage({ message: t`An error occurred while deleting the template. Please try again.` });
    }
  };

  const renderDeleteCell = (value, row) => {
    return (
      <Tooltip
        title={
          row.disableDelete
            ? t`Cannot delete template in use`
            : t`Delete template`
        }
      >
        {/* The span is crucial!
        It captures the hover event even when the button inside is disabled.
      */}
        <span>
          <IconButton
            disabled={row.disableDelete}
            onClick={handleDeleteClick(row)}
          >
            <Delete />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const openExportDialog = () => {
    setImportExportDialogMode('export');
  };

  const openImportDialog = () => {
    setImportExportDialogMode('import');
  };

  const closeImportExportDialog = () => {
    setImportExportDialogMode(null);
  };

  const updateParamTemplateName = async (templateId, newName) => {

    try {
      await updateBacNetParameterTemplate({id: templateId, data:{name: newName} });
      getSiteBacNetParameterTemplates(siteId);
    } catch (error) {
      console.error('Error updating template name:', error);
      addMessage({ message: t`An error occurred while updating the template name. Please try again.` });
    }

  };

  const updateUITemplateName = async (templateId, newName) => {
    try {
      await updateUiTemplate({
        templateId:templateId,
        payload: { name: newName },
      });
      refetchUiTemplates();
    } catch (error) {
      console.error('Error updating UI template name:', error);
      addMessage({ message: t`An error occurred while updating the UI template name. Please try again.` });
    }
  };

  const renderNameCellWithEdit = (value, row) => {

    const handleSave = async (newValue) => {


      const { valid, message } = validateTemplateName(newValue, value);

      if (!valid) {
        if(message){
          addMessage({ message });
        }
        return;
      }

      if (row.type === TEMPLATE_TYPE.PARAM_TEMPLATE) {
        updateParamTemplateName(row.id, newValue);
      } else {
        updateUITemplateName(row.id, newValue);
      }

    };
    return (
      <EditInPlaceCell
        value={value}
        onSave={handleSave}
      />
    );

  };

  const columnsConfig = [
    { id: 'name', label: t`Template Name`, renderCellContent: renderNameCellWithEdit, width: '35ch' },
    {
      id: 'vendorId',
      label: t`Vendor (ID)`,
      renderCellContent: (value, row) => {
        const vendorName = row.vendorName ?? t`Unknown Vendor`;
        return `${vendorName} (${value})`;
      },
    },
    { id: 'model', label: t`Model` },
    {
      id: 'type',
      label: t`Type`,
      renderCellContent: (value) => DISPLAY_TEMPLATE_TYPE[value],
    },
    {
      id: 'delete',
      label: null,
      width: 50,
      renderCellContent: renderDeleteCell,
    },
  ];

  const handleExportClick = () => {
    openExportDialog();
  };

  const handleImportClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try{
          const jsonData = await readJsonFile(file);
          const {success, message} = validateJSONForImport(jsonData);
          if (success) {
            setImportedData(jsonData.templates);
            openImportDialog();
          } else {
            addMessage({ message });
          }
        }catch (error) {
          console.error('Error importing templates:', error);
          addMessage({ message: t`An error occurred while importing templates. Please try a different file.` });
        }
      }
    };
    input.click();
  };

  const handleImportSuccess = ({ param = 0, ui = 0 }) => {
    confirm({
      title: t`Import Successful`,
      bodyMessage: (
        <div style={{textAlign: 'left'}}>
          <div>{t`Templates have been successfully imported.`}</div>
          <div>{t`Parameter Templates imported: ${param}.`}</div>
          {ui !== 0 && <div>{t`UI Templates imported: ${ui}.`}</div>}
        </div>
      ),
      confirmText: t`OK`,
      cancelText: null,
    });
    getSiteBacNetParameterTemplates(siteId);
    refetchUiTemplates();
  };

  return (
    <div className={classes.pageContainer}>
      <div className={classes.header}>
        <BackButton onClick={onClose} />
        <div className={classes.headerContainer}>
          <Typography
            className={classes.headerText}
          >{t`Template Management`}</Typography>
          <div>
            <IconButton onClick={handleExportClick} title={t`Export Templates to a JSON File`}>
              <ExportIcon />
            </IconButton>
            <IconButton onClick={handleImportClick} title={t`Import Templates from a JSON File`}>
              <ImportIcon />
            </IconButton>
          </div>
        </div>
      </div>
      <div className={classes.content}>
        <div className={classes.TableContainer}>
          <TableWithConfig
            stickyHeader
            columnsConfig={columnsConfig}
            nestingConfig={nestingConfig}
            rows={templatesData}
          />
        </div>
      </div>
      <ImportExportDialog
        templatesData={templatesData}
        open={!!importExportDialogMode}
        onClose={closeImportExportDialog}
        importedData={importedData}
        mode={importExportDialogMode}
        onImportComplete={handleImportSuccess}
      />
    </div>
  );
};

TemplateManagement.propTypes = {};

export default TemplateManagement;
